
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// export function formatCurrency(amount: number | null | undefined): string {
//   if (amount === null || amount === undefined) return '—';
  
//   // Format as crores with correct suffix
//   if (Math.abs(amount) >= 10000000) {
//     const crores = amount / 10000000;
//     return `₹${crores.toFixed(2)} Cr`;
//   }
  
//   // Format as regular currency
//   return new Intl.NumberFormat('en-IN', {
//     style: 'currency',
//     currency: 'INR',
//     maximumFractionDigits: 0,
//   }).format(amount).replace('₹', '₹ ');
// }

export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';

  const crores = amount / 10000000;
  return `₹${crores.toFixed(2)} Cr`;
}

export function formatValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return formatCurrency(value);
}

const ONE_CRORE = 10_000_000;

// Format large amounts in Lakhs with 2 decimal places
export function formatLakhs(n: number | undefined | null, prefix = "₹"): string {
  return `${prefix}${(Math.round((n || 0) / 1000) / 100).toFixed(2)}L`;
}

/** Lakhs below ₹1 Cr; compact crore above (e.g. ₹1cr, ₹1.2cr). */
export function formatLakhsOrCr(n: number | undefined | null, prefix = "₹"): string {
  const amount = n ?? 0;
  if (Math.abs(amount) >= ONE_CRORE) {
    const cr = amount / ONE_CRORE;
    const compact = parseFloat(cr.toFixed(2));
    return `${prefix}${compact}cr`;
  }
  return formatLakhs(amount, prefix);
}

export function displayLineItemName(key: string, isLLPIN: boolean = false): string {
  // LLPIN-specific mappings
  const llpinLineItemMap: Record<string, string> = {
    // Balance Sheet - Assets (LLPIN)
    'tangible_assets': 'Gross Fixed Assets',
    'noncurrent_investments': 'Investments',
    'long_term_loans_and_advances': 'Loans and Advances',
    'trade_receivables': 'Debtors / Trade Receivables',
    'cash_and_bank_balances': 'Cash and Cash Equivalents',
    'other_current_assets': 'Others Assets',
    'given_assets_total': 'Total',
    
    // Balance Sheet - Liabilities (LLPIN)
    'share_capital': 'Contribution Received',
    'reserves_and_surplus': 'Reserves and Surplus',
    'long_term_borrowings': 'Secured Loans',
    'other_long_term_liabilities': 'Unsecured Loans',
    'short_term_borrowings': 'Short Term Borrowing',
    'trade_payables': 'Creditor / Trade Payables - Advance from Customers',
    'other_current_liabilities': 'Other Liabilities',
    'short_term_provisions': 'Provisions',
    'given_liabilities_total': 'Total',
  };

  // CIN/Standard mappings
  const lineItemMap: Record<string, string> = {
    // Balance Sheet - Assets
    'tangible_assets': 'Tangible Assets',
    'producing_properties': 'Producing Properties',
    'intangible_assets': 'Intangible Assets',
    'preproducing_properties': 'Preproducing Properties',
    'tangible_assets_capital_work_in_progress': 'Tangible Assets - Capital Work in Progress',
    'intangible_assets_under_development': 'Intangible Assets Under Development',
    'noncurrent_investments': 'Non-Current Investments',
    'deferred_tax_assets_net': 'Deferred Tax Assets (Net)',
    'foreign_curr_monetary_item_trans_diff_asset_account': 'Foreign Currency Monetary Item Translation Difference Asset Account',
    'long_term_loans_and_advances': 'Long-Term Loans and Advances',
    'other_noncurrent_assets': 'Other Non-Current Assets',
    'current_investments': 'Current Investments',
    'inventories': 'Inventories',
    'trade_receivables': 'Trade Receivables',
    'cash_and_bank_balances': 'Cash and Bank Balances',
    'short_term_loans_and_advances': 'Short-Term Loans and Advances',
    'other_current_assets': 'Other Current Assets',
    'given_assets_total': 'Total Assets',
    
    // Balance Sheet - Liabilities
    'share_capital': 'Share Capital',
    'reserves_and_surplus': 'Reserves and Surplus',
    'money_received_against_share_warrants': 'Money Received Against Share Warrants',
    'share_application_money_pending_allotment': 'Share Application Money Pending Allotment',
    'deferred_government_grants': 'Deferred Government Grants',
    'minority_interest': 'Minority Interest',
    'long_term_borrowings': 'Long-Term Borrowings',
    'deferred_tax_liabilities_net': 'Deferred Tax Liabilities (Net)',
    'foreign_curr_monetary_item_trans_diff_liability_account': 'Foreign Currency Monetary Item Translation Difference Liability Account',
    'other_long_term_liabilities': 'Other Long-Term Liabilities',
    'long_term_provisions': 'Long-Term Provisions',
    'short_term_borrowings': 'Short-Term Borrowings',
    'trade_payables': 'Trade Payables',
    'other_current_liabilities': 'Other Current Liabilities',
    'short_term_provisions': 'Short-Term Provisions',
    'given_liabilities_total': 'Total Liabilities',
    
    // P&L Line Items
    'net_revenue': 'Net Revenue',
    'operating_cost': 'Operating Cost',
    'total_cost_of_materials_consumed': 'Total Cost of Materials Consumed',
    'total_purchases_of_stock_in_trade': 'Total Purchases of Stock in Trade',
    'total_changes_in_inventories_or_finished_goods': 'Total Changes in Inventories or Finished Goods',
    'total_employee_benefit_expense': 'Total Employee Benefit Expense',
    'total_other_expenses': 'Total Other Expenses',
    'operating_profit': 'Operating Profit',
    'other_income': 'Other Income',
    'depreciation': 'Depreciation',
    'profit_before_interest_and_tax': 'Profit Before Interest and Tax',
    'interest': 'Interest',
    'profit_before_tax_and_exceptional_items_before_tax': 'Profit Before Tax and Exceptional Items Before Tax',
    'exceptional_items_before_tax': 'Exceptional Items Before Tax',
    'profit_before_tax': 'Profit Before Tax',
    'income_tax': 'Income Tax',
    'profit_for_period_from_continuing_operations': 'Profit for Period from Continuing Operations',
    'profit_from_discontinuing_operation_after_tax': 'Profit from Discontinuing Operation After Tax',
    'minority_interest_and_profit_from_associates_and_joint_ventures': 'Minority Interest and Profit from Associates and Joint Ventures',
    'profit_after_tax': 'Profit After Tax',
    
    // Revenue Breakup
    'revenue_from_operations': 'Revenue from Operations',
    'revenue_from_interest': 'Revenue from Interest',
    'revenue_from_other_financial_services': 'Revenue from Other Financial Services',
    'revenue_from_sale_of_products': 'Revenue from Sale of Products',
    'revenue_from_sale_of_services': 'Revenue from Sale of Services',
    'other_operating_revenues': 'Other Operating Revenues',
    'excise_duty': 'Excise Duty',
    'service_tax_collected': 'Service Tax Collected',
    'other_duties_taxes_collected': 'Other Duties/Taxes Collected',
    
    // Cash Flow
    'adjustment_for_finance_cost_and_depreciation': 'Adjustment for Finance Cost and Depreciation',
    'adjustment_for_current_and_non_current_assets': 'Adjustment for Current and Non-Current Assets',
    'adjustment_for_current_and_non_current_liabilities': 'Adjustment for Current and Non-Current Liabilities',
    'other_adjustments_in_operating_activities': 'Other Adjustments in Operating Activities',
    'cash_flows_from_used_in_operating_activities': 'Cash Flows from/(Used in) Operating Activities',
    'cash_outflow_from_purchase_of_assets': 'Cash Outflow from Purchase of Assets',
    'cash_inflow_from_sale_of_assets': 'Cash Inflow from Sale of Assets',
    'income_from_assets': 'Income from Assets',
    'other_adjustments_in_investing_activities': 'Other Adjustments in Investing Activities',
    'cash_flows_from_used_in_investing_activities': 'Cash Flows from/(Used in) Investing Activities',
    'cash_outflow_from_repayment_of_capital_and_borrowings': 'Cash Outflow from Repayment of Capital and Borrowings',
    'cash_inflow_from_raisng_capital_and_borrowings': 'Cash Inflow from Raising Capital and Borrowings',
    'interest_and_dividends_paid': 'Interest and Dividends Paid',
    'other_adjustments_in_financing_activities': 'Other Adjustments in Financing Activities',
    'cash_flows_from_used_in_financing_activities': 'Cash Flows from/(Used in) Financing Activities',
    'incr_decr_in_cash_cash_equv_before_effect_of_excg_rate_changes': 'Increase/(Decrease) in Cash & Cash Equivalents Before Effect of Exchange Rate Changes',
    'adjustments_to_cash_and_cash_equivalents': 'Adjustments to Cash and Cash Equivalents',
    'incr_decr_in_cash_cash_equv': 'Net Increase/(Decrease) in Cash & Cash Equivalents',
    'cash_flow_statement_at_end_of_period': 'Cash & Cash Equivalents at End of Period',
    
    // Subtotals
    'total_operating_cost': 'Total Operating Cost'
  };
  
  // Use LLPIN mappings if isLLPIN is true and mapping exists, otherwise use standard mappings
  if (isLLPIN && llpinLineItemMap[key]) {
    return llpinLineItemMap[key];
  }
  
  return lineItemMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}
